// File: DiscussionForm.tsx
import React, { useEffect, useState } from 'react';
import { Input, Dropdown } from 'semantic-ui-react';
import ReactPaginate from 'react-paginate';
import { discussion as discussionAPI } from '@rap/api';
import { useAppSelector } from '../../redux/hooks';
import { selectLoginStatus, selectUserInfo } from '../../redux/userSlice';
import { getUniqueTags, getRandomAuthor, formatDate, getUniqueTitles } from './utils';
import { CommentSection, Comment } from './CommentSection';
import DiscussionModal from './DiscussionModal';
import './discussionForm.css';

const DiscussionForm: React.FC = () => {
  const [title, setTitle] = useState('');
  const [search, setSearch] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [type, setType] = useState<'note' | 'discussion'>('discussion');
  const [comments, setComments] = useState<Comment[]>([]);
  const [visibleReplies, setVisibleReplies] = useState<Set<number>>(new Set());
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyAnonymous, setReplyAnonymous] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [notify, setNotify] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sort, setSort] = useState<'latest' | 'oldest'>('latest');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const commentsPerPage = 4;
  const [open, setOpen] = useState(false);
  const [MyDiscussions, setMyDiscussions] = useState<number[]>([]);
  const [MyReplies, setMyReplies] = useState<number[]>([]);

  const loginStatus = useAppSelector(selectLoginStatus);
  const userInfo = useAppSelector(selectUserInfo);
  const isAdmin = userInfo?.permission_group.includes('admin');

  useEffect(() => {
    discussionAPI.getDiscussions().then((response) => {
      const updatedDiscussions = response.data.discussion.map((discussion: any) => ({
        ...discussion,
        timestamp: discussion.created_at,
        replies: discussion.replies.map((reply: any) => ({
          ...reply,
          timestamp: reply.creation_time,
        })),
      }));
      setComments(updatedDiscussions);
    });

    discussionAPI.getDiscussionbyUser().then((response) => {
      const userDiscussions = response.data.discussion.map((discussion: any) => discussion._id);
      setMyDiscussions(userDiscussions);
    });

    discussionAPI.getDiscussionReplyByUser().then((response) => {
      const userReplies = response.data.discussion;
      setMyReplies(userReplies);
    });
  }, []);

  const handleAddTag = () => {
    if (tagInput.trim()) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // const authorName = anonymous ? getRandomAuthor() : userInfo?.username || 'anonymous';

    const newPost = {
      title,
      content,
      anonymous: anonymous,
      tags,
      type,
      notify: notify,
      role: '',
    };

    discussionAPI.postDiscussion(newPost).then(() => location.reload());
  };

  const handleReplyClick = (commentId: number) => {
    setVisibleReplies(prev => {
      const newSet = new Set(prev);
      newSet.has(commentId) ? newSet.delete(commentId) : newSet.add(commentId);
      return newSet;
    });
  };

  const handleReplySubmit = (parentId: number) => {
    const reply: Comment = {
      _id: comments.length + 1,
      author: replyAnonymous.toString(),
      admin: false,
      title: '',
      content: replyContent,
      tags: [],
      type: 'note',
      replies: [],
      timestamp: formatDate(new Date()).toString(),
      role: '',
    };

    discussionAPI.postReply({ content: reply.content, anonymous: reply.author === 'true' }, parentId.toString())
        .then(() => location.reload());
  };

  const renderReplies = (replies: Comment[]) => (
      replies.map(reply => (
          <li key={reply._id} className="reply">
            <strong>{reply.author}</strong>
            {reply.admin && <span className="admin-badge">Admin</span>}
            {(reply.role != '') && <span className="role-badge">{reply.role}</span>}
            {(isAdmin || MyReplies.includes(reply._id)) && (
                <button className="reply-delete-button" title="Delete" onClick={() => handleReplyDelete(reply._id)}>
                  Delete
                </button>
            )}
            <p className="reply-content">{reply.content}</p>
          </li>
      ))
  );

  const handleReply = (commentId: number) => setReplyTo(commentId);
  const handleReplyContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setReplyContent(e.target.value);
  const handleReplyAnonymityChange = (e: React.ChangeEvent<HTMLInputElement>) => setReplyAnonymous(e.target.value === 'anonymous');
  const handleDelete = (id: number) => discussionAPI.deleteDiscussion(id.toString()).then(() => location.reload());
  const handleReplyDelete = (id: number) => {
    const parentComment = comments.find(comment => comment.replies.some(reply => reply._id === id));
    if (parentComment) {
      discussionAPI.deleteDiscussionReply(parentComment._id.toString(), id.toString()).then(() => location.reload());
    }
  };

  const filteredComments = comments.filter(comment =>
      (comment.content.toLowerCase().includes(search.toLowerCase()) ||
          comment.title.toLowerCase().includes(search.toLowerCase())) &&
      (selectedTags.length === 0 || selectedTags.some(tag => comment.tags.includes(tag)))
  ).sort((a, b) => sort === 'latest'
      ? new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      : new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const offset = currentPage * commentsPerPage;
  const paginatedComments = filteredComments.slice(offset, offset + commentsPerPage);
  const pageCount = Math.ceil(filteredComments.length / commentsPerPage);

  const uniqueTags = getUniqueTags(comments);
  const uniqueTitles = getUniqueTitles(comments);

  return (
      <div className="discussion-form">
        <h1>Discussion Forum</h1>
        <div className="analytics">
          <p>Welcome to the Discussion Page! Feel free to share any comments or questions about financial statement
            preparation and auditing issues. Remember to be kind and respectful of others. Happy Discussing!</p>
        </div>
        {loginStatus === 'success' ? (
            <button className="open-info-button" onClick={() => setOpen(true)}>Make a Post</button>
        ) : (
            <p className="warning-message">Please sign in to participate in the discussion forum.</p>
        )}


        <DiscussionModal
            open={open}
            setOpen={setOpen}
            handleSubmit={handleSubmit}
            title={title}
            setTitle={setTitle}
            content={content}
            setContent={setContent}
            tagInput={tagInput}
            setTagInput={setTagInput}
            tags={tags}
            setTags={setTags}
            handleAddTag={handleAddTag}
            handleRemoveTag={handleRemoveTag}
            handleAnonymityChange={(e, data) => setAnonymous(data.checked)}
            handleNotifyChange={(e, data) => setNotify(data.checked)}
            type={type}
            handleTypeChange={(e, data) => setType(data.value)}
            existingTags={uniqueTags}
        />

        <h2>Recent Discussions:</h2>

        <div className="row-container">

        <Input
            icon='search'
            placeholder='Search for discussions...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fluid
            style={{ marginBottom: '20px' }}
        />

        <Dropdown
            placeholder='Select Tags'
            fluid
            multiple
            selection
            options={uniqueTags.map(tag => ({ key: tag, text: tag, value: tag }))}
            onChange={(e, { value }) => setSelectedTags(value as string[])}
            value={selectedTags}
            clearable
            style={{ marginBottom: '20px' }}
        />

        <Dropdown
            placeholder='Sort by'
            fluid
            selection
            options={[
              { key: 'latest', text: 'Latest', value: 'latest' },
              { key: 'oldest', text: 'Oldest', value: 'oldest' },
            ]}
            value={sort}
            onChange={(e, { value }) => setSort(value as 'latest' | 'oldest')}
            style={{ marginBottom: '20px' }}
        />
        </div>

        <CommentSection
            comments={paginatedComments}
            visibleReplies={visibleReplies}
            handleReplyClick={handleReplyClick}
            renderReplies={renderReplies}
            replyTo={replyTo}
            replyContent={replyContent}
            handleReplyContentChange={handleReplyContentChange}
            handleReplySubmit={handleReplySubmit}
            handleReplyAnonymityChange={handleReplyAnonymityChange}
            isAdmin={!!isAdmin}
            loginStatus={loginStatus}
            MyDiscussions={MyDiscussions}
            MyReplies={MyReplies}
            handleDelete={handleDelete}
            handleReplyDelete={handleReplyDelete}
            replyAnonymous={replyAnonymous}
            handleReply={handleReply}
        />

        <ReactPaginate
            previousLabel={'Previous'}
            nextLabel={'Next'}
            breakLabel={'...'}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={({ selected }) => setCurrentPage(selected)}
            containerClassName={'pagination'}
            activeClassName={'active'}
        />
      </div>
  );
};

export default DiscussionForm;
